/**
 * SearchPage Component
 *
 * Main search results page with filters, grid/map views, and sorting.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { PublicLayout, Container } from '../../components/layout';
import {
  FilterPanel,
  FilterChips,
  MobileFilterDrawer,
  ResultsGrid,
  ResultsMap,
  SearchHeader,
  SortDropdown,
} from './components';
import { useSearchFilters, useSearchResults } from './hooks';
import { useSavedListings } from '../Dashboard/hooks';

export const SearchPage: React.FC = () => {
  const {
    filters,
    sort,
    setFilters,
    setSort,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  } = useSearchFilters();

  const { listings, isLoading, totalCount } = useSearchResults(filters, sort);
  const { toggleSave, savedIds } = useSavedListings();

  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Pagination: 12 listings per page (grid view only; the map shows all pins)
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(listings.length / PAGE_SIZE));

  // Back to page 1 whenever the result set changes
  useEffect(() => {
    setPage(0);
  }, [filters, sort, listings.length]);

  const pagedListings = useMemo(
    () => listings.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [listings, page]
  );

  const goToPage = (next: number) => {
    setPage(Math.min(Math.max(next, 0), totalPages - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalGuests =
    filters.guests.adults + filters.guests.children + filters.guests.infants;

  // Determine sort value for dropdown
  const sortValue =
    sort.field === 'price'
      ? sort.direction === 'asc'
        ? 'price_asc'
        : 'price_desc'
      : sort.field === 'rating'
      ? 'rating'
      : sort.field === 'createdAt'
      ? 'newest'
      : 'recommended';

  return (
    <PublicLayout>
      <div className="min-h-screen bg-white">
        <Container className="py-6">
          {/* Search Header */}
          <SearchHeader
            location={filters.location}
            checkIn={filters.checkIn}
            checkOut={filters.checkOut}
            guests={totalGuests || 1}
            resultCount={totalCount}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            activeFilterCount={activeFilterCount}
          />

          <div className="flex gap-4 lg:gap-8">
            {/* Desktop Filter Panel */}
            <div className="hidden lg:block">
              <FilterPanel
                filters={filters}
                onChange={setFilters}
                onClear={clearFilters}
              />
            </div>

            {/* Results Area */}
            <div className="flex-1 min-w-0">
              {/* Sort and Filter Chips */}
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  {hasActiveFilters && (
                    <FilterChips filters={filters} onChange={setFilters} />
                  )}
                </div>
                <SortDropdown value={sortValue} onChange={setSort} />
              </div>

              {/* Results Display */}
              {viewMode === 'grid' ? (
                <ResultsGrid
                  listings={pagedListings}
                  isLoading={isLoading}
                  onSave={toggleSave}
                  savedIds={savedIds}
                />
              ) : (
                <div className="h-[calc(100vh-280px)] min-h-[500px]">
                  <ResultsMap
                    listings={listings}
                    onSave={toggleSave}
                    savedIds={savedIds}
                  />
                </div>
              )}

              {/* Pagination */}
              {!isLoading && viewMode === 'grid' && listings.length > 0 && (
                <div className="mt-8 flex flex-col items-center gap-3">
                  {totalPages > 1 && (
                    <nav className="flex items-center gap-1" aria-label="Pagination">
                      <button
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 0}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i)
                        .filter(
                          (i) =>
                            i === 0 ||
                            i === totalPages - 1 ||
                            Math.abs(i - page) <= 1
                        )
                        .map((i, idx, arr) => (
                          <React.Fragment key={i}>
                            {idx > 0 && arr[idx - 1] !== i - 1 && (
                              <span className="px-1 text-gray-400">…</span>
                            )}
                            <button
                              onClick={() => goToPage(i)}
                              aria-current={i === page ? 'page' : undefined}
                              className={`min-w-[40px] px-3 py-2 text-sm rounded-lg border ${
                                i === page
                                  ? 'bg-primary-500 border-primary-500 text-white'
                                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {i + 1}
                            </button>
                          </React.Fragment>
                        ))}
                      <button
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= totalPages - 1}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  )}
                  <p className="text-sm text-gray-500">
                    Showing {page * PAGE_SIZE + 1}-
                    {Math.min((page + 1) * PAGE_SIZE, listings.length)} of{' '}
                    {totalCount} results
                  </p>
                </div>
              )}
            </div>
          </div>
        </Container>

        {/* Mobile Filter Drawer */}
        <MobileFilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onChange={setFilters}
          onClear={clearFilters}
          resultCount={totalCount}
        />
      </div>
    </PublicLayout>
  );
};

export default SearchPage;
